import {
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import client, { forBuilding, throwCaughtError } from "../client";
import { Building, BuildingManager, ExtendedUser, TimeString } from "@/types";
import { env } from "@/lib";
import { getBuildingId } from "@/lib";
import {
  useBuildingQuery,
  useBuildingSuspenseQuery,
} from "@/components/RouterSetup";

export const BuildingQueryFn = async (buildingId?: string) => {
  try {
    const response = await client.get("/getBuilding", forBuilding(buildingId));
    return response.data as Building;
  } catch (error) {
    throwCaughtError(error);
  }
};

export const buildingOptions = {
  queryKey: ["building"],
  queryFn: () => BuildingQueryFn(),
  refetchOnWindowFocus: false,
};

export const useBuilding = (): UseSuspenseQueryResult<Building> => {
  return useBuildingSuspenseQuery({
    ...buildingOptions,
    staleTime: 1000 * 60 * 60,
  });
};

export const usePublicBuildingData =
  (): UseSuspenseQueryResult<Building | null> => {
    return useBuildingSuspenseQuery({
      queryKey: ["building", "public"],
      queryFn: async () => {
        try {
          const buildingId = getBuildingId();
          const response = await fetch(
            `${env("VITE_API_BASE")}/api/getPublicBuilding`,
            {
              headers: { BUILDING: buildingId },
            },
          );
          return await response.json();
        } catch (error) {
          console.error(error);
          return null;
        }
      },
    });
  };

export const usePropertyManager =
  (): UseSuspenseQueryResult<BuildingManager | null> => {
    return useBuildingSuspenseQuery({
      queryKey: ["property-manager"],
      queryFn: async () => {
        try {
          const response = await client.get("/getPublicPropertyManager");
          return response.data;
        } catch (error) {
          console.error(error);
          return null;
        }
      },
    });
  };

export const buildingOpsTeamOptions = () => ({
  queryKey: ["building-ops-team"],
  queryFn: async () => {
    try {
      const response = await client.get("/getBuildingOpsTeam");
      return response.data.users;
    } catch (error) {
      throwCaughtError(error);
    }
  },
});

export const buildingAdminsOptions = (includeReceptionists = false) => ({
  queryKey: ["building-admins"],
  queryFn: async () => {
    try {
      const response = await client.get("/getBuildingAdmins", {
        params: { includeReceptionists },
      });
      return response.data.users as ExtendedUser[];
    } catch (error) {
      console.error(error);
      return [];
    }
  },
});

export const useBuildingAdmins = (): UseSuspenseQueryResult<ExtendedUser[]> => {
  return useBuildingSuspenseQuery(buildingAdminsOptions());
};

export const useBuildingReceptionists = (): UseQueryResult<ExtendedUser[]> => {
  return useBuildingQuery({
    queryKey: ["building-receptionists"],
    queryFn: async () => {
      try {
        const response = await client.get("/getBuildingReceptionists");
        return response.data.users;
      } catch (error) {
        throwCaughtError(error);
      }
    },
  });
};

export const updateBuildingSettings = async (updateData: Partial<Building>) => {
  return await client.patch("/updateBuilding", { updateData });
};

export const useCompaniesAccessCodeMapping = (
  accessControlSystemIntegratedForBuilding: boolean,
  buildingReception: boolean,
) => {
  return useBuildingQuery({
    queryKey: ["companies-access-code-mapping"],
    queryFn: async () => {
      try {
        const response = await client.get(
          "/getCompaniesAccessCodeForVisitorsEnabledMapping",
        );
        return response.data;
      } catch (error) {
        throwCaughtError(error);
      }
    },
    enabled: accessControlSystemIntegratedForBuilding && buildingReception,
  });
};

export const munuDealIdsOptions = {
  queryKey: ["munu-deal-ids"],
  queryFn: async () => {
    try {
      const response = await client.get("/getMunuDealIds");
      return response.data;
    } catch (error) {
      throwCaughtError(error);
    }
  },
};

export const useMunuDealIds = (): UseSuspenseQueryResult<
  {
    id: string;
    name: string;
  }[]
> => {
  return useBuildingSuspenseQuery(munuDealIdsOptions);
};

export const addBuildingAdmin = async (userId: string) => {
  return client.post(`/addBuildingAdmin?userId=${userId}`);
};

export const updateOpeningHours = async (
  openingHour: TimeString,
  closingHour: TimeString,
) => {
  return await client.patch("/updateBuilding", {
    updateData: { openingHour, closingHour },
  });
};

export const removeBuildingAdmin = async (userId: string) => {
  return client.post(`/removeBuildingAdmin?userId=${userId}`);
};

export const assignBuildingAccessAdmin = async (userId: string) => {
  return client.post(`/assignBuildingAccessAdmin?userId=${userId}`);
};

export const supplierSnackAdminsOptions = () => ({
  queryKey: ["supplier-snack-admins"],
  queryFn: async () => {
    try {
      const response = await client.get("/getSupplierSnackAdmins");
      return response.data.users as ExtendedUser[];
    } catch (error) {
      console.error(error);
      return [];
    }
  },
});

export const supplierFoodAdminsOptions = () => ({
  queryKey: ["supplier-food-admins"],
  queryFn: async () => {
    try {
      const response = await client.get("/getSupplierFoodAdmins");
      return response.data.users as ExtendedUser[];
    } catch (error) {
      console.error(error);
      return [];
    }
  },
});

export const addSupplierSnackAdmin = async (userId: string) => {
  return client.post(`/addSupplierSnackAdmin?userId=${userId}`);
};

export const removeSupplierSnackAdmin = async (userId: string) => {
  return client.post(`/removeSupplierSnackAdmin?userId=${userId}`);
};

export const addSupplierFoodAdmin = async (userId: string) => {
  return client.post(`/addSupplierFoodAdmin?userId=${userId}`);
};

export const removeSupplierFoodAdmin = async (userId: string) => {
  return client.post(`/removeSupplierFoodAdmin?userId=${userId}`);
};

export type SupplierAdminsBackfillSummary = {
  totalBuildings: number;
  backfilledCount: number;
  skippedCount: number;
  failedCount: number;
  backfilled: {
    buildingId: string;
    displayName?: string;
    seededSnackAdmins?: number;
    seededFoodAdmins?: number;
  }[];
  skipped: { buildingId: string; displayName?: string; reason?: string }[];
  failed: { buildingId: string; error?: string }[];
};

/** Welcome-ops only: one-time backfill of supplier-admin whitelists across all buildings. */
export const backfillSupplierAdmins =
  async (): Promise<SupplierAdminsBackfillSummary> => {
    const response = await client.post("/backfillSupplierAdmins");
    return response.data as SupplierAdminsBackfillSummary;
  };

export const revokeBuildingAccessAdmin = async (userId: string) => {
  return client.post(`/revokeBuildingAccessAdmin?userId=${userId}`);
};

export const removeBuildingReceptionist = async (userId: string) => {
  return client.post(`/removeBuildingReceptionist`, { userId });
};

export const addBuildingReceptionist = async (userId: string) => {
  return client.post(`/addBuildingReceptionist`, { userId });
};

export const getAllBuildings = async () => {
  const response = await client.get("/getAllBuildings");
  return response.data.buildingIds || [];
};

export const useAllBuildings = () =>
  useBuildingSuspenseQuery({
    queryKey: ["getAllBuildings"],
    queryFn: getAllBuildings,
  });

export const toggleCommsForBuildingAdmins = async () => {
  return client.post("/toggleCommsForBuildingAdmins");
};
export const toggleCommsForUsers = async () => {
  return client.post("/toggleCommsForUsers");
};
export const toggleWelcomeFood = async (enabled: boolean) => {
  return client.post("/toggleWelcomeFood", { enabled });
};
export const toggleFoodWalletRefund = async () => {
  return client.post("/toggleFoodWalletRefund");
};
export const toggleMunu = async () => {
  return client.post("/toggleMunu");
};
export const toggleMillum = async () => {
  return client.post("/toggleMillum");
};
export const toggleBooking = async () => {
  return client.post("/toggleBooking");
};
export const toggleBookingVisibleForUsers = async () => {
  return client.post("/toggleBookingVisibleForUsers");
};
export const toggleAccess = async () => {
  return client.post("/toggleAccess");
};
export const toggleDashboardEnabledForAdmins = async () => {
  return client.post("/toggleDashboardEnabledForAdmins");
};
export const toggleDashboardEnabledForUsers = async () => {
  return client.post("/toggleDashboardEnabledForUsers");
};
export const toggleParking = async () => {
  return client.post("/toggleParking");
};
export const toggleQuarterlyUserPurge = async () => {
  return client.post("/toggleQuarterlyUserPurge");
};
export const toggleSnack = async (enabled: boolean) => {
  return client.post("/toggleSnack", { enabled });
};
export const toggleSnackForUsers = async () => {
  return client.post("/toggleSnackForUsers");
};
export const createNewBuilding = async (
  name: string,
  email: string,
  owner: string,
) => {
  return client.post("/createBuilding", { name, email, owner });
};

export const useGetBuildingDisplayNameById = (
  buildingIds: string[],
  userId?: string,
) =>
  useBuildingQuery({
    queryKey: [
      "building-display-names",
      userId ?? null,
      ...buildingIds.slice().sort(),
    ],
    queryFn: async () => {
      if (!userId) {
        return {};
      }

      try {
        const response = await client.get("/getBuildingDisplayNameById", {
          params: { buildingIds, userId },
        });
        return response.data as Record<string, string>;
      } catch (error) {
        throwCaughtError(error);
        return {};
      }
    },
    enabled: buildingIds.length > 0 && Boolean(userId),
  });
export type BuildingContact = {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
};

export type BuildingInfoWithStats = Building & {
  id: string;
  companyCount: number | null;
  userCount: number | null;
  propertyManager: BuildingContact | null;
  operationsContact: BuildingContact | null;
};

export const allBuildingInfoOptions = {
  queryKey: ["getAllBuildingInfo"],
  queryFn: async () => {
    const response = await client.get("/getAllBuildingInfo");
    return response.data.buildingInfo as BuildingInfoWithStats[];
  },
  refetchOnWindowFocus: false,
};
export const useAllBuildingInfo = () => {
  return useSuspenseQuery(allBuildingInfoOptions);
};
